import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import { R } from '@/modules/api/actions'
import { AuthContext } from '@/modules/auth/authContext'
import {
    AdminLandingPageListItem,
    DuplicateLandingPageRequestBody,
    DuplicateLandingPageResponse,
    UpdateLandingPageRequestBody,
} from '@storefronts/common-types'

/**
 * TanStack Query hooks for fetching and updating landing pages
 * These mutation hooks update the query cache with optimistic updates/rollbacks
 * https://tanstack.com/query/v5/docs/framework/react/guides/optimistic-updates
 **/

export const LANDING_PAGES_QUERY_KEY = 'landing-pages'
export const LANDING_PAGE_TEMPLATES_QUERY_KEY = 'landing-page-templates'
export const LANDING_PAGE_QUERY_KEY = 'landing-page'
export const LANDING_PAGE_LINKS_QUERY_KEY = 'landing-page-links'

export function landingPagesQueryKey(clientHash: string | null | undefined) {
    return [LANDING_PAGES_QUERY_KEY, clientHash ?? null] as const
}

export function landingPageQueryKey(
    landingPageHash: string,
    clientHash: string | null | undefined,
) {
    return [
        LANDING_PAGE_QUERY_KEY,
        landingPageHash,
        clientHash ?? null,
    ] as const
}

export function landingPageLinksQueryKey(
    clientHash: string | null | undefined,
) {
    return [LANDING_PAGE_LINKS_QUERY_KEY, clientHash ?? null] as const
}

function useClientHash(): string | null {
    const { client } = useContext(AuthContext)
    return client?.clientHash ?? null
}

interface UseLandingPagesQueryOptions {
    enabled?: boolean
    refetchInterval?: number
}

function useLandingPagesQuery({
    enabled = true,
    refetchInterval,
}: UseLandingPagesQueryOptions = {}) {
    const clientHash = useClientHash()

    return useQuery({
        queryKey: landingPagesQueryKey(clientHash),
        queryFn: async () => {
            const response = await R.getAllLandingPages()
            return response.landingPages
        },
        enabled: enabled && Boolean(clientHash),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchInterval,
    })
}

function useLandingPageTemplatesQuery(enabled = true) {
    return useQuery({
        queryKey: [LANDING_PAGE_TEMPLATES_QUERY_KEY],
        queryFn: async () => {
            const response = await R.getAllLandingPageTemplates()
            return response.templates
        },
        enabled,
        staleTime: 5 * 60 * 1000,
        retry: 2,
    })
}

interface UseUpdateLandingPageMutationOptions {
    onSuccess?: (data: any) => void
    onError?: (error: Error) => void
}

function useUpdateLandingPageMutation({
    onSuccess,
    onError,
}: UseUpdateLandingPageMutationOptions = {}) {
    const queryClient = useQueryClient()
    const listQueryKey = landingPagesQueryKey(useClientHash())

    return useMutation({
        mutationFn: async (data: UpdateLandingPageRequestBody) => {
            return await R.updateLandingPage(data)
        },
        onMutate: async (newData: UpdateLandingPageRequestBody) => {
            await queryClient.cancelQueries({
                queryKey: listQueryKey,
            })

            const previousData =
                queryClient.getQueryData<AdminLandingPageListItem[]>(
                    listQueryKey,
                )

            queryClient.setQueryData<AdminLandingPageListItem[]>(
                listQueryKey,
                (oldData) => {
                    if (!oldData) return oldData

                    return oldData.map((landingPage) => {
                        if (
                            landingPage.landingPageHash ===
                            newData.landingPageHash
                        ) {
                            return {
                                ...landingPage,
                                ...newData,
                            } as AdminLandingPageListItem
                        }
                        return landingPage
                    })
                },
            )

            return { previousData }
        },
        onSuccess,
        onError: (err, _newData, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(listQueryKey, context.previousData)
            }
            onError?.(err)
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: listQueryKey,
            })
        },
    })
}

interface UseDuplicateLandingPageMutationOptions {
    onSuccess?: (data: DuplicateLandingPageResponse) => void
    onError?: (error: Error) => void
}

function useDuplicateLandingPageMutation({
    onSuccess,
    onError,
}: UseDuplicateLandingPageMutationOptions = {}) {
    const queryClient = useQueryClient()
    const listQueryKey = landingPagesQueryKey(useClientHash())

    return useMutation({
        mutationFn: async (data: DuplicateLandingPageRequestBody) => {
            return await R.duplicateLandingPage(data)
        },
        onMutate: async () => {
            await queryClient.cancelQueries({
                queryKey: listQueryKey,
            })

            const previousData =
                queryClient.getQueryData<AdminLandingPageListItem[]>(
                    listQueryKey,
                )

            return { previousData }
        },
        onSuccess: (data) => {
            queryClient.setQueryData<AdminLandingPageListItem[]>(
                listQueryKey,
                (oldData) => {
                    const listItem = {
                        ...data.landingPage,
                        tags: [],
                        sourceAd: null,
                    }
                    if (!oldData) return [listItem]
                    return [...oldData, listItem]
                },
            )

            onSuccess?.(data)
        },
        onError: (err, _newData, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(listQueryKey, context.previousData)
            }
            onError?.(err)
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: listQueryKey,
            })
        },
    })
}

function useLandingPageQuery(
    landingPageHash: string | undefined,
    enabled = true,
) {
    const clientHash = useClientHash()

    return useQuery({
        queryKey: landingPageQueryKey(landingPageHash as string, clientHash),
        queryFn: async () => {
            const response = await R.getLandingPage(landingPageHash as string)
            return response.landingPage
        },
        enabled: enabled && !!landingPageHash && Boolean(clientHash),
        staleTime: 5 * 60 * 1000,
        retry: 2,
    })
}

export const STOREFRONT_AI_FEEDBACK_ELIGIBILITY_QUERY_KEY =
    'storefront-ai-feedback-eligibility'

/** Whether the page can be rated, and the AI stamp each rating must echo. */
function useStorefrontAiFeedbackEligibilityQuery(
    landingPageHash: string | undefined,
) {
    const clientHash = useClientHash()

    return useQuery({
        queryKey: [
            STOREFRONT_AI_FEEDBACK_ELIGIBILITY_QUERY_KEY,
            landingPageHash,
            clientHash,
        ],
        queryFn: () =>
            R.getStorefrontAiFeedbackEligibility(landingPageHash as string),
        enabled: !!landingPageHash && Boolean(clientHash),
        staleTime: 5 * 60 * 1000,
        retry: 2,
    })
}

function useLandingPageOrderLineItemSummaryQuery(
    landingPageHash: string | undefined,
    enabled: boolean,
) {
    const clientHash = useClientHash()

    return useQuery({
        queryKey: [
            'landing-page-order-line-item-summary',
            landingPageHash,
            clientHash,
        ],
        queryFn: async () => {
            const response = await R.getLandingPageOrderLineItemSummary(
                landingPageHash as string,
            )
            return response.products
        },
        enabled: enabled && !!landingPageHash && Boolean(clientHash),
        staleTime: 5 * 60 * 1000,
        retry: 2,
    })
}

function useLandingPageSourceAdQuery(landingPageHash: string | undefined) {
    const clientHash = useClientHash()

    return useQuery({
        queryKey: ['landing-page-source-ad', landingPageHash, clientHash],
        queryFn: async () => {
            const response = await R.getLandingPageSourceAd(
                landingPageHash as string,
            )
            return response.sourceAd
        },
        enabled: !!landingPageHash && Boolean(clientHash),
        staleTime: 5 * 60 * 1000,
        retry: 2,
    })
}

export {
    useLandingPagesQuery,
    useLandingPageTemplatesQuery,
    useLandingPageQuery,
    useLandingPageOrderLineItemSummaryQuery,
    useLandingPageSourceAdQuery,
    useStorefrontAiFeedbackEligibilityQuery,
    useUpdateLandingPageMutation,
    useDuplicateLandingPageMutation,
}
